/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Global command palette (⌘K) powered by cmdk + tRPC search.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { useThemeContext } from '@/contexts/ThemeContext';
import { isRetroUnlocked } from '@/lib/achievements';
import { analytics } from '@/lib/analytics';
import type { SearchResult } from '@/server/trpc/search';

import HomeIcon from '@mui/icons-material/Home';
import ArticleIcon from '@mui/icons-material/Article';
import CodeIcon from '@mui/icons-material/Code';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ExploreIcon from '@mui/icons-material/Explore';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import TerminalIcon from '@mui/icons-material/Terminal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import BuildIcon from '@mui/icons-material/Build';

const typeIcons: Record<string, React.ReactNode> = {
  page: <ExploreIcon sx={{ fontSize: 18 }} />,
  post: <ArticleIcon sx={{ fontSize: 18 }} />,
  project: <CodeIcon sx={{ fontSize: 18 }} />,
  quote: <FormatQuoteIcon sx={{ fontSize: 18 }} />,
  article: <AutoStoriesIcon sx={{ fontSize: 18 }} />,
  profile: <WorkIcon sx={{ fontSize: 18 }} />,
  uses: <BuildIcon sx={{ fontSize: 18 }} />,
};

const typeLabels: Record<string, string> = {
  page: 'Pages',
  post: 'Blog Posts',
  project: 'Projects',
  quote: 'Quotes',
  article: 'Product Engineering',
  profile: 'About / Experience',
  uses: 'Tools & Setup',
};

function groupResults(results: SearchResult[]) {
  const groups: Record<string, SearchResult[]> = {};
  for (const r of results) {
    (groups[r.type] ??= []).push(r);
  }
  return groups;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [retroAvailable, setRetroAvailable] = useState(false);
  const navigate = useNavigate();
  const { mode, isRetro, toggleMode, setRetro } = useThemeContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const queryRef = useRef(query);
  queryRef.current = query;

  const closePalette = useCallback((reason: 'escape' | 'overlay' | 'selection' | 'keyboard') => {
    setOpen((prev) => {
      if (prev) analytics.commandPaletteClosed(reason, queryRef.current);
      return false;
    });
  }, []);

  useEffect(() => {
    setRetroAvailable(isRetroUnlocked());
    const handler = () => setRetroAvailable(true);
    window.addEventListener('achievement-unlocked', handler);
    return () => window.removeEventListener('achievement-unlocked', handler);
  }, []);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: results } = useQuery({
    ...trpc.search.query.queryOptions({ q: query }),
    enabled: open && query.length >= 1,
    placeholderData: (prev: any) => prev,
    staleTime: 30_000,
  });

  const grouped = useMemo(() => groupResults(results ?? []), [results]);

  useEffect(() => {
    if (!open || query.length < 1) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      analytics.commandPaletteSearch(query, results?.length ?? 0);
    }, 500);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [open, query, results]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => {
          if (!prev) {
            analytics.commandPaletteOpened(e.isTrusted ? 'keyboard' : 'button');
            return true;
          }
          analytics.commandPaletteClosed('keyboard', queryRef.current);
          return false;
        });
      }
      if (e.key === 'Escape') {
        closePalette('escape');
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closePalette]);

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const handleSelect = useCallback(
    (value: string) => {
      if (value === '__toggle-theme') {
        analytics.commandPaletteSelected('action', 'Toggle Theme', query);
        toggleMode();
        closePalette('selection');
        return;
      }
      if (value === '__copy-url') {
        analytics.commandPaletteSelected('action', 'Copy URL', query);
        navigator.clipboard.writeText(window.location.href);
        closePalette('selection');
        return;
      }
      if (value === '__toggle-retro') {
        analytics.commandPaletteSelected('action', 'Toggle Retro', query);
        setRetro(!isRetro);
        closePalette('selection');
        return;
      }
      if (value === '__show-connections') {
        analytics.commandPaletteSelected('action', 'Show Connections', query);
        window.dispatchEvent(new Event('show-connections'));
        closePalette('selection');
        return;
      }
      if (value === '__easter-egg') {
        analytics.commandPaletteSelected('action', 'Easter Egg', query);
        navigate({ to: '/the-void' as any });
        closePalette('selection');
        return;
      }
      if (value.startsWith('/')) {
        const matchedResult = results?.find((r) => r.url === value);
        analytics.commandPaletteSelected(
          matchedResult?.type ?? 'page',
          matchedResult?.title ?? value,
          query,
        );

        const hashIndex = value.indexOf('#');
        if (hashIndex !== -1) {
          const path = value.slice(0, hashIndex);
          const hash = value.slice(hashIndex + 1);
          navigate({ to: path || '/' });
          closePalette('selection');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          });
        } else {
          navigate({ to: value });
          closePalette('selection');
        }
      }
    },
    [navigate, toggleMode, setRetro, isRetro, query, results, closePalette],
  );

  const quickActions = useMemo(() => {
    const actions = [
      {
        value: '__toggle-theme',
        label: `Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`,
        keywords: 'theme dark light mode toggle switch',
        icon: mode === 'dark' ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />,
      },
      {
        value: '__copy-url',
        label: 'Copy current URL',
        keywords: 'copy url link clipboard share',
        icon: <ContentCopyIcon sx={{ fontSize: 18 }} />,
      },
      ...(retroAvailable ? [{
        value: '__toggle-retro',
        label: `${isRetro ? 'Disable' : 'Enable'} Retro Mode`,
        desc: isRetro ? 'Switch back to normal theme' : 'Terminal hacker aesthetic',
        keywords: 'retro terminal hacker mode aesthetic crt',
        icon: <TerminalIcon sx={{ fontSize: 18 }} />,
      }] : []),
      {
        value: '__show-connections',
        label: 'Show Connections',
        desc: 'Google & Spotify sign-in',
        keywords: 'connections google spotify sso sign in login auth account',
        icon: <LinkIcon sx={{ fontSize: 18 }} />,
      },
      {
        value: '__easter-egg',
        label: 'Get Lost in Space',
        desc: 'Find the Easter egg...',
        keywords: 'easter egg space void secret hidden',
        icon: <RocketLaunchIcon sx={{ fontSize: 18 }} />,
      },
    ];
    return actions;
  }, [mode, isRetro, retroAvailable]);

  const navItems = useMemo(() => [
    { url: '/', label: 'Home', keywords: 'home landing main', icon: <HomeIcon sx={{ fontSize: 18 }} /> },
    { url: '/about', label: 'About', keywords: 'about me bio christian', icon: <ExploreIcon sx={{ fontSize: 18 }} /> },
    { url: '/blog', label: 'Blog', keywords: 'blog posts writing articles', icon: <ArticleIcon sx={{ fontSize: 18 }} /> },
    { url: '/prodeng', label: 'Product Engineering', keywords: 'product engineering prodeng articles series', icon: <AutoStoriesIcon sx={{ fontSize: 18 }} /> },
    { url: '/projects', label: 'Projects', keywords: 'projects code open source github', icon: <CodeIcon sx={{ fontSize: 18 }} /> },
    { url: '/music', label: 'Music Analyzer', keywords: 'music audio analyzer spotify librosa', icon: <ExploreIcon sx={{ fontSize: 18 }} /> },
    { url: '/quotes', label: 'Quotes', keywords: 'quotes inspiration sayings', icon: <FormatQuoteIcon sx={{ fontSize: 18 }} /> },
  ], []);

  const matchesQuery = useCallback((item: { label: string; keywords: string; desc?: string }, q: string) => {
    if (!q) return true;
    const lower = q.toLowerCase();
    return (
      item.label.toLowerCase().includes(lower) ||
      item.keywords.toLowerCase().includes(lower) ||
      (item.desc?.toLowerCase().includes(lower) ?? false)
    );
  }, []);

  if (!open) return null;

  const hasQuery = query.length >= 1;
  const hasResults = results && results.length > 0;
  const filteredActions = quickActions.filter((a) => matchesQuery(a, query));
  const filteredNav = navItems.filter((n) => matchesQuery(n, query));
  const hasLocalResults = filteredActions.length > 0 || filteredNav.length > 0;

  return (
    <div className="cmdk-overlay" onClick={() => closePalette('overlay')}>
      <div className="cmdk-container" onClick={(e) => e.stopPropagation()}>
        <Command label="Command palette" shouldFilter={false}>
          <div className="cmdk-input-wrapper">
            <SearchIcon sx={{ fontSize: 20, opacity: 0.5 }} />
            <Command.Input
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              placeholder="Search pages, posts, projects, quotes..."
              autoFocus
            />
            <kbd className="cmdk-kbd">ESC</kbd>
          </div>

          <Command.List>
            {hasQuery && !hasResults && !hasLocalResults && (
              <Command.Empty>No results found for &ldquo;{query}&rdquo;</Command.Empty>
            )}

            {/* Quick Actions (client-filtered) */}
            {filteredActions.length > 0 && (
              <Command.Group heading="Quick Actions">
                {filteredActions.map((action) => (
                  <Command.Item key={action.value} value={action.value} onSelect={handleSelect}>
                    <span className="cmdk-item-icon">{action.icon}</span>
                    <span className="cmdk-item-text">
                      {action.desc ? (
                        <>
                          <span className="cmdk-item-title">{action.label}</span>
                          <span className="cmdk-item-desc">{action.desc}</span>
                        </>
                      ) : action.label}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Navigation (client-filtered) */}
            {filteredNav.length > 0 && (
              <Command.Group heading="Navigate">
                {filteredNav.map((item) => (
                  <Command.Item key={item.url} value={item.url} onSelect={handleSelect}>
                    <span className="cmdk-item-icon">{item.icon}</span>
                    <span className="cmdk-item-text">{item.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Server search results grouped by type */}
            {hasQuery &&
              (['page', 'article', 'post', 'project', 'profile', 'uses', 'quote'] as const).map((type) => {
                const items = grouped[type];
                if (!items?.length) return null;
                return (
                  <Command.Group key={type} heading={typeLabels[type]}>
                    {items.map((item) => (
                      <Command.Item
                        key={item.id}
                        value={item.url}
                        onSelect={handleSelect}
                      >
                        <span className="cmdk-item-icon">{typeIcons[type]}</span>
                        <span className="cmdk-item-text">
                          <span className="cmdk-item-title">{item.title}</span>
                          {item.description && (
                            <span className="cmdk-item-desc">{item.description}</span>
                          )}
                        </span>
                        {item.tags && item.tags.length > 0 && (
                          <span className="cmdk-item-tags">
                            {item.tags.slice(0, 3).map((t) => (
                              <span key={t} className="cmdk-tag">{t}</span>
                            ))}
                          </span>
                        )}
                      </Command.Item>
                    ))}
                  </Command.Group>
                );
              })}
          </Command.List>

          <div className="cmdk-footer">
            <span>
              <kbd>↑↓</kbd> navigate
            </span>
            <span>
              <kbd>↵</kbd> select
            </span>
            <span>
              <kbd>esc</kbd> close
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}
